import {
    Engagement,
    EngagementAnticipatedStart,
    EngagementRole,
    EngagementStatus,
    EngagementWorkload,
} from '../models'

import { calculateAssignmentRatePerWeek } from './assignment-rates.utils'

const STATUS_TO_API: Record<string, string> = {
    Active: 'ACTIVE',
    Cancelled: 'CANCELLED',
    Closed: 'CLOSED',
    'On Hold': 'ON_HOLD',
    Open: 'OPEN',
    'Pending Assignment': 'ON_HOLD',
}

const STATUS_FROM_API: Record<string, EngagementStatus> = {
    ACTIVE: 'Active',
    CANCELLED: 'Cancelled',
    CLOSED: 'Closed',
    ON_HOLD: 'On Hold',
    OPEN: 'Open',
    PENDING_ASSIGNMENT: 'On Hold',
}

const ROLE_TO_API: Record<string, string> = {
    DATA_ENGINEER: 'DATA_ENGINEER',
    DATA_SCIENTIST: 'DATA_SCIENTIST',
    DESIGNER: 'DESIGNER',
    SOFTWARE_DEVELOPER: 'SOFTWARE_DEVELOPER',
    SOFTWARE_ENGINEER: 'SOFTWARE_DEVELOPER',
}

const ROLE_FROM_API: Record<string, EngagementRole> = {
    DATA_ENGINEER: 'DATA_ENGINEER',
    DATA_SCIENTIST: 'DATA_SCIENTIST',
    DESIGNER: 'DESIGNER',
    SOFTWARE_DEVELOPER: 'SOFTWARE_DEVELOPER',
}

const WORKLOAD_TO_API: Record<string, string> = {
    FRACTIONAL: 'FRACTIONAL',
    FULL_TIME: 'FULL_TIME',
    PART_TIME: 'FRACTIONAL',
}

const WORKLOAD_FROM_API: Record<string, EngagementWorkload> = {
    FRACTIONAL: 'FRACTIONAL',
    FULL_TIME: 'FULL_TIME',
    PART_TIME: 'FRACTIONAL',
}

const ANTICIPATED_START_LABELS: Record<string, string> = {
    FEW_DAYS: 'In a few days',
    FEW_WEEKS: 'In a few weeks',
    IMMEDIATE: 'Immediate',
}

const TERMINAL_ASSIGNMENT_STATUSES = new Set([
    'COMPLETED',
    'OFFER_REJECTED',
    'TERMINATED',
])

function normalizeString(value: unknown): string {
    if (typeof value !== 'string') {
        return ''
    }

    return value.trim()
}

function toUpperSnake(value: string): string {
    return value
        .trim()
        .replace(/[\s-]+/g, '_')
        .toUpperCase()
}

function toIsoString(value: unknown): string {
    if (typeof value === 'string') {
        return value
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString()
    }

    return ''
}

function toNumber(value: unknown, fallback: number = 0): number {
    const parsed = Number(value)

    if (!Number.isFinite(parsed)) {
        return fallback
    }

    return parsed
}

function toOptionalString(value: unknown): string | undefined {
    if (typeof value === 'string') {
        const normalized = value.trim()

        return normalized || undefined
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value)
    }

    return undefined
}

function toOptionalNumberishValue(value: unknown): number | string | undefined {
    if (typeof value === 'number') {
        return Number.isFinite(value)
            ? value
            : undefined
    }

    if (typeof value === 'string') {
        const normalized = value.trim()

        return normalized || undefined
    }

    return undefined
}

function toIdentifierValue(...values: unknown[]): number | string {
    for (const value of values) {
        if (typeof value === 'number' && Number.isFinite(value)) {
            return value
        }

        if (typeof value === 'string') {
            const normalized = value.trim()

            if (normalized) {
                return normalized
            }
        }
    }

    return ''
}

function normalizeSkillValue(value: unknown): string {
    if (typeof value === 'string') {
        return value.trim()
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return String(value)
            .trim()
    }

    return ''
}

function normalizeSkill(skill: unknown): Engagement['skills'][number] | undefined {
    if (typeof skill === 'string' || typeof skill === 'number') {
        const normalizedId = normalizeSkillValue(skill)

        if (!normalizedId) {
            return undefined
        }

        return {
            id: normalizedId,
            name: normalizedId,
        }
    }

    if (!skill || typeof skill !== 'object') {
        return undefined
    }

    const typedSkill = skill as {
        id?: unknown
        label?: unknown
        name?: unknown
        value?: unknown
    }
    const normalizedId = normalizeSkillValue(typedSkill.id ?? typedSkill.value)

    if (!normalizedId) {
        return undefined
    }

    const normalizedName = normalizeString(
        typedSkill.name ?? typedSkill.label ?? normalizedId,
    ) || normalizedId

    return {
        id: normalizedId,
        name: normalizedName,
    }
}

type AssignmentInput = Record<string, unknown>

function normalizeEngagementSkills(data: Partial<Engagement>): Engagement['skills'] {
    const normalizedData = data as Partial<Engagement> & {
        requiredSkills?: unknown[]
    }

    const rawSkills = Array.isArray(data.skills) && data.skills.length
        ? data.skills
        : (Array.isArray(normalizedData.requiredSkills)
            ? normalizedData.requiredSkills
            : [])

    return rawSkills
        .map(skill => normalizeSkill(skill))
        .filter((skill): skill is Engagement['skills'][number] => !!skill)
}

function normalizeDurationWeeks(data: Partial<Engagement>): number {
    if (data.durationWeeks !== undefined) {
        return toNumber(data.durationWeeks)
    }

    const legacyData = data as Partial<Engagement> & {
        durationAmount?: unknown
        durationUnit?: unknown
        duration?: {
            amount?: unknown
            lengthInWeeks?: unknown
            unit?: unknown
        } | unknown
    }

    const legacyDuration = legacyData.duration

    const durationUnit = normalizeString(
        (legacyDuration && typeof legacyDuration === 'object'
            ? (legacyDuration as { unit?: unknown }).unit
            : undefined) ?? legacyData.durationUnit,
    )
        .toLowerCase()

    const durationAmount = (legacyDuration && typeof legacyDuration === 'object'
        ? (legacyDuration as { amount?: unknown }).amount
        : undefined) ?? legacyData.durationAmount

    if (durationUnit === 'weeks' && durationAmount !== undefined) {
        return toNumber(durationAmount)
    }

    if (legacyDuration && typeof legacyDuration === 'object') {
        return toNumber((legacyDuration as {
            lengthInWeeks?: unknown
        }).lengthInWeeks)
    }

    return toNumber(legacyDuration)
}

export function normalizeEngagementAssignmentStatus(status: string): string {
    if (!status) {
        return ''
    }

    return status
        .toString()
        .trim()
        .toUpperCase()
        .replace(/[\s-]+/g, '_')
}

export function isEngagementAssignmentCompletionStatus(status: string): boolean {
    const normalizedStatus = normalizeEngagementAssignmentStatus(status)

    if (!normalizedStatus) {
        return false
    }

    return TERMINAL_ASSIGNMENT_STATUSES.has(normalizedStatus)
}

export function isCountableEngagementAssignmentStatus(status: string): boolean {
    return !isEngagementAssignmentCompletionStatus(status)
}

export function getCountableEngagementAssignments(
    assignments: Engagement['assignments'] = [],
): Engagement['assignments'] {
    if (!Array.isArray(assignments)) {
        return []
    }

    return assignments.filter(assignment => {
        const status = assignment.status || ''

        return isCountableEngagementAssignmentStatus(String(status))
    })
}

// eslint-disable-next-line complexity
function normalizeAssignment(
    assignment: AssignmentInput,
    engagementId: Engagement['id'] | undefined,
): Engagement['assignments'][number] {
    const ratePerHour = toOptionalString(
        assignment.ratePerHour ?? assignment.rate_per_hour,
    )
    const standardHoursPerWeek = toOptionalNumberishValue(
        assignment.standardHoursPerWeek ?? assignment.standard_hours_per_week,
    )
    const agreementRate = toOptionalString(
        assignment.agreementRate
        ?? assignment.agreement_rate
        ?? assignment.rate
        ?? calculateAssignmentRatePerWeek(ratePerHour, standardHoursPerWeek),
    )

    return {
        agreementRate: agreementRate || '',
        durationMonths: toOptionalNumberishValue(
            assignment.durationMonths ?? assignment.duration_months,
        ),
        endDate: toIsoString(
            assignment.endDate
            ?? assignment.end_date
            ?? assignment.end,
        ),
        engagementId: toIdentifierValue(
            assignment.engagementId,
            assignment.engagement_id,
            engagementId,
        ),
        id: toIdentifierValue(
            assignment.id,
            assignment.assignmentId,
            assignment.assignment_id,
        ),
        memberHandle: normalizeString(
            assignment.memberHandle
            ?? assignment.member_handle
            ?? assignment.handle,
        ),
        memberId: toIdentifierValue(
            assignment.memberId,
            assignment.member_id,
            assignment.userId,
            assignment.user_id,
        ),
        otherRemarks: normalizeString(
            assignment.otherRemarks
            ?? assignment.other_remarks
            ?? assignment.remarks,
        ),
        ratePerHour,
        standardHoursPerWeek,
        startDate: toIsoString(
            assignment.startDate
            ?? assignment.start_date
            ?? assignment.start,
        ),
        status: normalizeString(
            assignment.assignmentStatus
            ?? assignment.assignment_status
            ?? assignment.assignmentState
            ?? assignment.status,
        ),
        terminationReason: normalizeString(
            assignment.terminationReason
            ?? assignment.termination_reason,
        ) || undefined,
        termsAccepted: assignment.termsAccepted === true
            || assignment.terms_accepted === true,
    }
}

function normalizeAssignments(data: Partial<Engagement>): Engagement['assignments'] {
    if (!Array.isArray(data.assignments)) {
        return []
    }

    return data.assignments
        .filter((assignment): assignment is NonNullable<typeof assignment> => !!assignment)
        .map(assignment => normalizeAssignment(assignment as unknown as AssignmentInput, data.id))
}

// eslint-disable-next-line complexity
export function normalizeEngagement(data: Partial<Engagement> = {}): Engagement {
    const assignments = normalizeAssignments(data)

    const skills = normalizeEngagementSkills(data)

    const assignedMemberHandles = Array.isArray(data.assignedMemberHandles)
        ? data.assignedMemberHandles
            .map(value => normalizeString(value))
            .filter(Boolean)
        : []

    const countries = Array.isArray(data.countries)
        ? data.countries
            .map(value => normalizeString(value))
            .filter(Boolean)
        : []

    const timezones = Array.isArray(data.timezones)
        ? data.timezones
            .map(value => normalizeString(value))
            .filter(Boolean)
        : []

    const anticipatedStart = data.anticipatedStart
        ? fromEngagementAnticipatedStartApi(data.anticipatedStart)
        : 'IMMEDIATE'

    const status = data.status
        ? fromEngagementStatusApi(data.status)
        : 'Open'

    const role = data.role
        ? fromEngagementRoleApi(data.role)
        : 'DESIGNER'

    const workload = data.workload
        ? fromEngagementWorkloadApi(data.workload)
        : 'FULL_TIME'

    const rawProject = (data as {
        project?: {
            id?: number | string
            name?: string
        }
    }).project

    const project = rawProject
        ? {
            id: rawProject.id,
            name: normalizeString(rawProject.name),
        }
        : undefined

    const projectName = normalizeString(
        (data as {
            projectName?: string
        }).projectName || project?.name,
    )

    return {
        anticipatedStart,
        applications: Array.isArray(data.applications)
            ? data.applications
            : [],
        applicationsCount: getApplicationsCount(data),
        assignedMemberHandles,
        assignments,
        compensationRange: normalizeString(data.compensationRange),
        countries,
        createdAt: toIsoString(data.createdAt),
        description: normalizeString(data.description),
        durationWeeks: normalizeDurationWeeks(data),
        id: data.id || '',
        isPrivate: data.isPrivate === true,
        project,
        projectId: data.projectId || project?.id || '',
        projectName,
        requiredMemberCount: toNumber(data.requiredMemberCount),
        role,
        skills,
        status,
        timezones,
        title: normalizeString(data.title),
        updatedAt: toIsoString(data.updatedAt),
        workload,
    }
}

export function formatAnticipatedStart(value: string | EngagementAnticipatedStart): string {
    if (!value) {
        return '-'
    }

    const key = toUpperSnake(String(value))

    return ANTICIPATED_START_LABELS[key] || value
}

export function formatEngagementStatus(value: string | EngagementStatus): string {
    const normalized = toUpperSnake(String(value || ''))

    if (!normalized) {
        return '-'
    }

    return STATUS_FROM_API[normalized] || value
}

export function formatDuration(engagement: Partial<Engagement>): string {
    if (engagement.durationWeeks && engagement.durationWeeks > 0) {
        return `${engagement.durationWeeks} weeks`
    }

    return '-'
}

export function getEngagementDurationInDays(engagement: Partial<Engagement>): number {
    if (!engagement.durationWeeks || engagement.durationWeeks <= 0) {
        return 0
    }

    return engagement.durationWeeks * 7
}

export function formatLocation(engagement: Partial<Engagement>): string {
    const countries = Array.isArray(engagement.countries)
        ? engagement.countries
            .map(value => normalizeString(value))
            .filter(Boolean)
        : []
    const timezones = Array.isArray(engagement.timezones)
        ? engagement.timezones
            .map(value => normalizeString(value))
            .filter(Boolean)
        : []

    if (!countries.length && !timezones.length) {
        return 'Remote'
    }

    if (!countries.length) {
        return timezones.join(', ')
    }

    if (!timezones.length) {
        return countries.join(', ')
    }

    return `${timezones.join(', ')} / ${countries.join(', ')}`
}

export function getAssignedMembersCount(engagement: Partial<Engagement>): number {
    const assignments = Array.isArray(engagement.assignments)
        ? engagement.assignments
        : []

    if (assignments.length) {
        return getCountableEngagementAssignments(assignments).length
    }

    const assignedMemberHandles = Array.isArray(engagement.assignedMemberHandles)
        ? engagement.assignedMemberHandles
            .map(value => normalizeString(value))
            .filter(Boolean)
        : []

    return assignedMemberHandles.length
}

export function hasCountableAssignments(engagement: Partial<Engagement>): boolean {
    const assignments = Array.isArray(engagement.assignments)
        ? engagement.assignments
        : []

    return getCountableEngagementAssignments(assignments).length > 0
}

export function getApplicationsCount(engagement: Partial<Engagement>): number {
    if (typeof engagement.applicationsCount === 'number') {
        return engagement.applicationsCount
    }

    if (Array.isArray(engagement.applications)) {
        return engagement.applications.length
    }

    const maybeCount = (engagement as {
        applicationCount?: unknown
        applications_count?: unknown
        count?: {
            applications?: unknown
        }
    })
    const legacyCount = Reflect.get(engagement as Record<string, unknown>, '_count') as {
        applications?: unknown
    } | undefined

    if (typeof maybeCount.applicationCount === 'number') {
        return maybeCount.applicationCount
    }

    if (typeof maybeCount.applications_count === 'number') {
        return maybeCount.applications_count
    }

    if (typeof maybeCount.count?.applications === 'number') {
        return maybeCount.count.applications
    }

    if (typeof legacyCount?.applications === 'number') {
        return legacyCount.applications
    }

    return 0
}

export function toEngagementStatusApi(status: string): string {
    const normalized = normalizeString(status)

    if (!normalized) {
        return ''
    }

    return STATUS_TO_API[normalized] || toUpperSnake(normalized)
}

export function fromEngagementStatusApi(status: string): EngagementStatus | string {
    const normalized = toUpperSnake(normalizeString(status))

    if (!normalized) {
        return 'Open'
    }

    return STATUS_FROM_API[normalized] || status
}

export function toEngagementRoleApi(role: string): string {
    const normalized = toUpperSnake(normalizeString(role))

    if (!normalized) {
        return ''
    }

    return ROLE_TO_API[normalized] || normalized
}

export function fromEngagementRoleApi(role: string): EngagementRole | string {
    const normalized = toUpperSnake(normalizeString(role))

    if (!normalized) {
        return 'DESIGNER'
    }

    return ROLE_FROM_API[normalized] || role
}

export function toEngagementWorkloadApi(workload: string): string {
    const normalized = toUpperSnake(normalizeString(workload))

    if (!normalized) {
        return ''
    }

    return WORKLOAD_TO_API[normalized] || normalized
}

export function fromEngagementWorkloadApi(workload: string): EngagementWorkload | string {
    const normalized = toUpperSnake(normalizeString(workload))

    if (!normalized) {
        return 'FULL_TIME'
    }

    return WORKLOAD_FROM_API[normalized] || workload
}

export function toEngagementAnticipatedStartApi(value: string): string {
    const normalized = toUpperSnake(normalizeString(value))

    if (!normalized) {
        return ''
    }

    if (normalized === 'IN_A_FEW_DAYS') {
        return 'FEW_DAYS'
    }

    if (normalized === 'IN_A_FEW_WEEKS') {
        return 'FEW_WEEKS'
    }

    return normalized
}

export function fromEngagementAnticipatedStartApi(
    value: string,
): EngagementAnticipatedStart | string {
    const normalized = toUpperSnake(normalizeString(value))

    if (!normalized) {
        return 'IMMEDIATE'
    }

    if (normalized === 'IN_A_FEW_DAYS') {
        return 'FEW_DAYS'
    }

    if (normalized === 'IN_A_FEW_WEEKS') {
        return 'FEW_WEEKS'
    }

    return normalized
}
